/**
 * CommunitiesSubTabs Component
 * Sub-tab navigation within the Communities tab: All, Saved.
 * All shows user's created and joined communities.
 * Saved shows user's bookmarked/saved communities.
 */

import { Search, X } from "lucide-react"
import { useState } from "react"
import { CommunityCard } from "@/components/cards/communityCard"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useUserCommunities } from "@/features/communities/hooks/useUserCommunities"
import { TabEmptyState } from "@/features/my-rituality/components/TabEmptyState"
import { useSavedCommunities } from "@/features/profile/hooks/useSavedCommunities"
import { useDebounce } from "@/hooks/useDebounce"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import type { CommunitiesSubTab } from "../types/my-rituality.types"

interface CommunitiesSubTabsProps {
  totalCount: number
  savedCount: number
  userId: string
  searchQuery?: string
  showEnhancedEmpty?: boolean
}

export function CommunitiesSubTabs({
  totalCount,
  savedCount,
  userId,
  searchQuery,
  showEnhancedEmpty = false,
}: CommunitiesSubTabsProps) {
  const { t } = useTranslation("myRituality")

  const [activeSubTab, setActiveSubTab] = useState<CommunitiesSubTab>("all")

  // Search state
  const [showSearch, setShowSearch] = useState(false)
  const [localSearch, setLocalSearch] = useState("")
  const debouncedLocalSearch = useDebounce(localSearch, 300)

  // Combine local search with parent searchQuery
  const effectiveSearch = debouncedLocalSearch || searchQuery || ""

  // All communities - infinite scroll
  const {
    data: allData,
    isLoading: allLoading,
    isError: allError,
    hasNextPage: allHasNextPage,
    fetchNextPage: allFetchNextPage,
    isFetchingNextPage: allFetchingNextPage,
  } = useUserCommunities(userId || "", {
    search: effectiveSearch,
    filter: "all",
    sort: "newest",
  })

  // Saved communities
  const {
    data: savedData,
    isLoading: savedLoading,
    isError: savedError,
  } = useSavedCommunities({
    limit: 50,
    search: effectiveSearch || undefined,
  })

  // Flatten all pages for all communities
  const allCommunities = allData?.pages.flatMap(page => page.items) ?? []

  // Transform saved communities to match CommunityCard props
  const savedCommunities =
    savedData?.communities.map(item => ({
      id: item.community.id,
      name: item.community.name,
      coverImage: item.community.coverImage ?? undefined,
      memberCount: item.community.memberCount ?? 0,
      ritualCount: item.community.ritualCount ?? 0,
      location: `${item.community.city ?? ""}${item.community.city && item.community.state ? ", " : ""}${item.community.state ?? ""}`,
      city: item.community.city ?? undefined,
      state: item.community.state ?? undefined,
      type: item.community.type,
      isSaved: true,
    })) ?? []

  // savedData?.total is available if needed for additional count display

  // Infinite scroll for all communities
  const loadMoreRef = useInfiniteScroll({
    onLoadMore: () => {
      if (allHasNextPage && !allFetchingNextPage) {
        allFetchNextPage()
      }
    },
    hasMore: allHasNextPage ?? false,
    threshold: 200,
  })

  const subTabs: { key: CommunitiesSubTab; label: string; count: number }[] = [
    { key: "all", label: t("communitiesSubTabs.all"), count: totalCount },
    { key: "saved", label: t("communitiesSubTabs.saved"), count: savedCount },
  ]

  const handleSubTabChange = (value: CommunitiesSubTab) => {
    setActiveSubTab(value)
    setLocalSearch("")
    setShowSearch(false)
  }

  const hasActiveFilters = effectiveSearch !== ""

  if (
    !allLoading &&
    !allError &&
    allCommunities.length === 0 &&
    showEnhancedEmpty &&
    !hasActiveFilters
  ) {
    return (
      <TabEmptyState
        title={t("communitiesSubTabs.emptyTitle")}
        description={t("communitiesSubTabs.emptyDescription")}
        ctaLabel={t("communitiesSubTabs.exploreCommunities")}
        ctaTo="/connect"
      />
    )
  }

  return (
    <div className="px-5 md:px-0">
      <div className="flex items-center justify-between">
        <div className="flex md:items-center flex-col w-full md:flex-row gap-1.25 md:gap-3.75">
          <div className="flex justify-between items-center gap-2">
            <div className="text-[18px] font-semibold leading-7.5 uppercase text-text-primary">
              {t("tabs.communities")}
            </div>
            <div className="md:hidden flex">
              {showSearch ? (
                <div className="flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 h-7.5">
                  <Search className="w-4 h-4 text-primary/50 shrink-0" />
                  <Input
                    type="text"
                    placeholder={t("tabs.search")}
                    value={localSearch}
                    onChange={e => setLocalSearch(e.target.value)}
                    className="max-w-48 border-none h-7.5 bg-transparent px-2 text-sm text-primary shadow-none focus-visible:ring-0"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-6 w-6 text-primary/50"
                    onClick={() => {
                      setShowSearch(false)
                      setLocalSearch("")
                    }}
                    aria-label={t("tabs.closeSearch")}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="opacity-50 cursor-pointer h-5"
                  onClick={() => setShowSearch(true)}
                  aria-label={t("tabs.search")}
                >
                  <Search className="w-5 h-5 text-primary" />
                </button>
              )}
            </div>
          </div>

          <div className="w-px h-7.5 bg-text-primary/10 hidden md:block" />

          {/* Sub-tabs */}
          <div className="flex items-center h-7.5 gap-3.75 md:gap-5 text-sm text-white overflow-x-auto scrollbar-hide">
            {subTabs.map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  requestAnimationFrame(() => handleSubTabChange(tab.key))
                }}
                className={cn(
                  "text-sm cursor-pointer whitespace-nowrap shrink-0",
                  activeSubTab === tab.key ? "font-semibold opacity-100" : "font-normal opacity-70",
                )}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      <p className="mt-1.25 md:mt-3 text-[14px] leading-[25px]">
        {activeSubTab === "all"
          ? t("communitiesTab.description")
          : t("communitiesSubTabs.savedDescription")}
      </p>

      {/* All Communities Content */}
      {activeSubTab === "all" && (
        <>
          {allLoading && (
            <div className="text-center py-8 text-stone">{t("communitiesSubTabs.loading")}</div>
          )}
          {allError && (
            <div className="text-center py-8 text-red">{t("communitiesSubTabs.error")}</div>
          )}
          {!allLoading &&
            !allError &&
            allCommunities.length === 0 &&
            showEnhancedEmpty &&
            hasActiveFilters && (
              <div className="text-center py-8 text-stone">{t("communitiesSubTabs.noResults")}</div>
            )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 my-7.5 md:my-10">
            {allCommunities.map(community => (
              <CommunityCard key={community.id} community={community} />
            ))}
          </div>
          {(allHasNextPage || allFetchingNextPage) && (
            <div ref={loadMoreRef} className="h-4" aria-hidden="true" />
          )}
          {allFetchingNextPage && (
            <div className="text-center py-4 text-stone text-sm">
              {t("communitiesSubTabs.loadingMore")}
            </div>
          )}
        </>
      )}

      {/* Saved Communities Content */}
      {activeSubTab === "saved" && (
        <>
          {savedLoading && (
            <div className="text-center py-8 text-stone">{t("communitiesSubTabs.loading")}</div>
          )}
          {savedError && (
            <div className="text-center py-8 text-red">{t("communitiesSubTabs.error")}</div>
          )}
          {!savedLoading && !savedError && savedCommunities.length === 0 && (
            <div className="text-center py-8 text-stone">
              {hasActiveFilters
                ? t("communitiesSubTabs.noSavedResults")
                : t("communitiesSubTabs.noSaved")}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 my-7.5 md:my-10">
            {savedCommunities.map(community => (
              <CommunityCard key={community.id} community={community} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
